import { useState, useEffect, useCallback, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { CompanyCredit } from "../CreditInterfaces";
import Icon from "../../../../components/Icon/Icon";
import { CreditType } from "../../Program/ProgramInterfaces";
import {
  deleteCompanyCredit,
  getCompanyCredits,
  getLinkedEntriesByCompanyCreditId,
  updateCompanyCredit,
  uploadCompanyCreditCsvFile,
} from "../manageCreditLibrary";
import SortCreditLibrary from "./SortCreditLibrary";
import FilterCreditLibrary from "./FilterCreditLibrary";
import CreditDetails from "./CreditDetails";

import FileUpload from "../../../../components/FormFields/FileUpload";
import {
  CreditListContainer,
  LibraryContainer,
  LibraryInnerContainer,
} from "../../../../components/Library/Library";
import {
  FileObj,
  SpinnerContainer,
} from "../../../../components/LocalFileUpload.tsx/LocalFileUpload";
import ProgressCircle from "../../../../components/ProgressCircle/ProgressCircle";
import AddCreditModal from "../AddCreditModal";
import config from "../../../../config";
import useTaskHandler, {
  BottomBar,
  PENDING,
  SelectableContainer,
  TASKS_COMPLETED,
  TaskHandlerReturnType,
} from "../../../../hooks/useTaskHandler";
import ToggleSwitchField from "../../../../components/FormFields/ToggleSwitchField";
import Button from "../../../../components/Button/Button";
import { Tag } from "../../../MediaLibrary/mediaLibrary.model";
import TagSelectionModal from "../../../../components/Tags/TagSelectionModal";
import { FormikProvider, useFormik } from "formik";
import assetsConfig from "../../../../assetsConfig";
import { useAlert } from "../../../../components/Alert/Alerts";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { copy } from "../../../../utils/copy";

export const CompanyCreditGridCSS = `
    display: grid;
    grid-template-areas: "a b c";
    grid-template-columns: 1fr 1fr 150px;

    :first-child {
      padding-left: 10px;
    }
  `;

const CompanyCreditContainer = styled(CreditListContainer)`
  .children {
    ${CompanyCreditGridCSS};
  }
`;

const CreditFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
  & > * {
    font-size: ${({ theme }) => theme.xxSmallSize};
    line-height: ${({ theme }) => theme.xxSmallSize};
    word-break: break-word;
  }
`;

// const SortContainer = styled.div`
//   grid-template-areas: "a b c d e";
//   grid-template-columns: 30px 2fr 1fr 1fr 2fr;
//   background: ${({ theme }) => theme.colorBackgroundDarkDark};
// `;
export const UploadProgress = (props: { uploadProgress: CSVStatus }) => {
  const [startUploadProgress, setStartUploadProgress] = useState(0);
  const [showUploadProgress, setShowUploadProgress] = useState(false);

  const totalProgress =
    props.uploadProgress === CSVStatus.Processing
      ? 50
      : props.uploadProgress === CSVStatus.Complete
      ? 100
      : 0;

  const statusMessage =
    props.uploadProgress === CSVStatus.Processing
      ? "Processing"
      : props.uploadProgress === CSVStatus.Complete
      ? "Complete"
      : null;

  useEffect(() => {
    setTimeout(() => {
      setStartUploadProgress(20);
    }, 250);
  }, []);

  useEffect(() => {
    if (props.uploadProgress === CSVStatus.Complete) {
      setTimeout(() => {
        setShowUploadProgress(false);
      }, 500);
    } else if (props.uploadProgress === CSVStatus.Processing) {
      setShowUploadProgress(true);
    }
  }, [props.uploadProgress]);

  return (
    <div className="flex gap-[2rem] items-center">
      <ProgressCircle
        value={Math.ceil(totalProgress)}
        text={`${Math.ceil(totalProgress)}%`}
        size="90px"
        showFullProgress
      />
      <div className="flex flex-col gap-[.5rem] text-left">
        <h2>Upload Progress</h2>
        <p>{statusMessage + "..."}</p>
        <p>
          You have an upload in progress. <br /> If you leave this page, your
          upload progress will be lost.
        </p>
      </div>
      <img
        className="!w-[90px]"
        src={config.assets.loading.tertiary}
        alt="loading..."
      />
    </div>
  );
};

const CompanyCreditList = (props: CompanyCreditListProps) => {
  const theme = useTheme();
  const { addNewAlert } = useAlert();
  const { labels } = useSelector((state: RootState) => state.labels);
  const [CompanyCredits, setCompanyCredits] = useState<CompanyCredit[]>([]);
  const [filteredCredits, setFilteredCredits] = useState<CompanyCredit[]>([]);
  const [sortedCredits, setSortedCredits] = useState<CompanyCredit[]>([]);
  const [csvStatus, setCsvStatus] = useState<CSVStatus>(CSVStatus.Idle);

  const [isUploading, setIsUploading] = useState(false);

  const [creditLibraryInstructions, setCreditLibraryInstructions] =
    useState<string>();

  useEffect(() => {
    copy("creditLibraryInstructions")
      .then((text) => {
        setCreditLibraryInstructions(text);
      })
      .catch();
  }, []);

  // multi-select functionality
  const [showTagModal, setShowTagModal] = useState(false);

  const deleteCredit = useCallback(
    (credit: CompanyCredit): Promise<CompanyCredit> => {
      return new Promise((resolve, reject) => {
        getLinkedEntriesByCompanyCreditId(props.companyId, credit.id!)
          .then((res) => {
            if (res.status === 200) {
              deleteCompanyCredit(credit, res.data, labels)
                .then(() => {
                  resolve(credit);
                })
                .catch((e) => reject(`Error Deleting Credit: ${e.message}`));
            }
          })
          .catch((e) => reject(`Error Deleting Credit: ${e.message}`));
      });
    },
    []
  );

  const formikProps = useFormik({
    initialValues: {
      allActive: false,
    },
    onSubmit: async (value) => {},
    enableReinitialize: true,
  });

  /*
  state.pending: Processing items array
  state.completed: Completed items array
  state.errored: Errored items array (includes item and error)
  state.next: currently processing item
  state.prev: last processed item
  */
  const {
    reset,
    loadItems,
    onSubmit,
    handleSelectItem,
    ...state
  }: TaskHandlerReturnType<CompanyCredit> = useTaskHandler<CompanyCredit>();

  // Add deletion tasks when 'Delete Selected' is clicked
  const handleDeleteSelected = () => {
    onSubmit(deleteCredit);
  };

  const [showAddCreditModal, setShowAddCreditModal] =
    useState<null | CreditType>(null);

  const handleGetCompanyCredits = () => {
    if (props.companyId) {
      getCompanyCredits(props.companyId).then((res) => {
        if (res.status === 200) {
          setCompanyCredits(res.data);
        }
      });
    }
  };

  const handleDeleteCompanyCredit = (credit: CompanyCredit) => {
    getLinkedEntriesByCompanyCreditId(props.companyId, credit.id!).then(
      (res) => {
        if (res.status === 200) {
          deleteCompanyCredit(credit, res.data, labels)
            .then(() => {
              addNewAlert({
                type: "success",
                message: "Successfully deleted company credit",
              });
              handleGetCompanyCredits();
            })
            .catch((e) =>
              addNewAlert({
                type: "error",
                message: e.message,
              })
            );
        }
      }
    );
  };

  const handleEditCredit = useCallback((credit: CompanyCredit) => {
    return new Promise<CompanyCredit>((resolve, reject) => {
      updateCompanyCredit(credit.id!, credit)
        .then(() => {
          resolve(credit);
        })
        .catch((e) => reject(`Error Editing Credit: ${e.message}`));
    });
  }, []);

  const toggleIsActive = (credit: CompanyCredit) => {
    updateCompanyCredit(credit.id!, credit)
      .then(() => handleGetCompanyCredits())
      .catch((e) =>
        addNewAlert({
          type: "error",
          message: e.message,
        })
      );
  };

  const handleTagSelected = (tags: Tag[], selectedCredit: CompanyCredit[]) => {
    const newArray = selectedCredit.map((credit) => {
      return { ...credit, tags: tags };
    });
    onSubmit(handleEditCredit, newArray);
  };

  const handleToggleActiveSelected = (
    isActive: boolean,
    selectedCredit: CompanyCredit[]
  ) => {
    const newArray = selectedCredit.map((credit) => {
      return { ...credit, isActive: isActive };
    });
    onSubmit(handleEditCredit, newArray);
  };

  const commonTags = useMemo(() => {
    if (state.items.length === 0) {
      return [];
    }

    const firstItemTags = state.items[0].tags || [];

    for (let i = 1; i < state.items.length; i++) {
      const mediaItem = state.items[i];

      // Compare tags arrays. If they differ, return empty array.
      if (
        !firstItemTags.every(
          (tag) =>
            mediaItem.tags &&
            mediaItem.tags.findIndex((x) => x.id === tag.id) > -1
        )
      ) {
        return [];
      }
    }

    // If we made it through the loop without returning, all tags are the same.
    return firstItemTags;
  }, [state.items]);

  const allActive = useMemo(() => {
    if (state.items.length === 0) {
      return false;
    }
    return state.items.every((x) => x.isActive);
  }, [state.items]);

  useEffect(() => {
    if (state.status === PENDING) {
      setIsUploading(true);
    }
    if (state.status === TASKS_COMPLETED) {
      // If there are no tasks left, refresh the media list
      handleGetCompanyCredits();
      // loadItems([]);
      setIsUploading(false);
    }
  }, [state.status]);

  useEffect(() => {
    handleGetCompanyCredits();
  }, [props.companyId]);

  const [creditForm, setCreditForm] = useState<CompanyCredit>();

  const [showCompanyCreditDetails, setShowCompanyCreditDetails] =
    useState(false);

  const handleEditIconClick = (data: CompanyCredit) => {
    setCreditForm(data);
    setShowCompanyCreditDetails(true);
  };

  const handleFileUpload = (files: FileObj[]) => {
    if (files.length > 1) {
      addNewAlert({
        type: "error",
        message: "Only one file can be uploaded at a time.",
      });

      return;
    }

    let latestFile = files.reduce((prev, curr) =>
      prev.id > curr.id ? prev : curr
    );

    setCsvStatus(CSVStatus.Processing);
    uploadCompanyCreditCsvFile(props.companyId, latestFile.file)
      .then((response) => {
        addNewAlert({
          type: "success",
          message: "Successfully imported CSV file.",
        });
        handleGetCompanyCredits();
        setCsvStatus(CSVStatus.Complete);
      })
      .catch((e) => {
        if (e?.response?.status == 400) {
          let badRequestMessage = e.response.data[0];

          addNewAlert({
            type: "error",
            message: badRequestMessage,
          });
          setCsvStatus(CSVStatus.Idle);

          return;
        }

        if (e?.response?.status == 500 && e.response?.data?.detail) {
          let detail = e.response?.data?.detail;

          addNewAlert({
            type: "error",
            message: detail,
          });
          setCsvStatus(CSVStatus.Idle);

          return;
        }

        addNewAlert({
          type: "error",
          message: "An error occurred, please try again.",
        });
        setCsvStatus(CSVStatus.Idle);
      });
  };

  useEffect(() => {
    if (csvStatus === CSVStatus.Complete) {
      setTimeout(() => {
        setCsvStatus(CSVStatus.Idle);
      }, 500);
    }
  }, [csvStatus]);

  useEffect(() => {
    handleGetCompanyCredits();
  }, [props.refreshCredits]);

  return (
    <FormikProvider value={formikProps}>
      <LibraryContainer>
        <FileUpload
          className="fileUploadBg"
          id="FileUpload-CompanyCreditList"
          onChange={(files) => handleFileUpload(files)}
          allowedMediaType={"csv"}
          fileTypeName={"CSV"}
          multiple={false}
          hideUpload={true}
          whileLoading={csvStatus !== CSVStatus.Idle}
          resetAfterOnChange={true}
          children={
            <div className="relative w-full">
              <SpinnerContainer
                show={csvStatus !== CSVStatus.Idle}
                background={theme.colorBackgroundLightLight}
              >
                {csvStatus !== CSVStatus.Idle && (
                  <UploadProgress uploadProgress={csvStatus} />
                )}
              </SpinnerContainer>
            </div>
          }
        />

        <FilterCreditLibrary
          credits={CompanyCredits}
          setCredits={(credits) => setFilteredCredits(credits)}
          creditType={CreditType.Company}
        />

        {creditLibraryInstructions && (
          <p
            className="m-[2rem] mb-0"
            dangerouslySetInnerHTML={{ __html: creditLibraryInstructions }}
          />
        )}

        <LibraryInnerContainer>
          <SortCreditLibrary
            credits={filteredCredits} // filter then sort
            setCredits={(credits) => setSortedCredits([...credits])}
            creditType={CreditType.Company}
          />

          {props.companyId && (
            <AddCreditModal
              show={showAddCreditModal !== null}
              onClickHide={() => setShowAddCreditModal(null)}
              companyId={props.companyId}
              setIndividualCreditsArray={() => {}}
              setCompanyCreditsArray={setCompanyCredits}
              defaultCreditTab={
                showAddCreditModal !== null
                  ? showAddCreditModal
                  : CreditType.Company
              }
            />
          )}

          {showTagModal && state.items.length && (
            <TagSelectionModal
              closeModal={(tags, cancelled) => {
                if (!cancelled) {
                  handleTagSelected(tags, state.items);
                }
                setShowTagModal(false);
              }}
              companyId={props.companyId}
              inputTags={commonTags}
              title="Add Tags to Credits"
            />
          )}

          <CompanyCreditContainer>
            {sortedCredits.map((credit: CompanyCredit) => (
              <SelectableContainer
                state={state}
                table={true}
                item={credit}
                key={credit.id}
                onClick={(event) => {
                  if (event.shiftKey) {
                    handleSelectItem(credit);
                    window?.getSelection()?.removeAllRanges();
                  }
                }}
              >
                {/* <CheckboxField
                    className="!pl-[2rem]"
                    name={`${credit.id}.isActive`}
                  /> */}
                <CreditFieldContainer className="!pl-[2rem]">
                  <span className="font-bold">{credit.name}</span>
                  <span>
                    {credit.otherCompanyType
                      ? credit.otherCompanyType
                      : credit.companyType}
                  </span>
                </CreditFieldContainer>
                <CreditFieldContainer>
                  <span className="font-bold">{credit.city}</span>
                  <span>{credit.country}</span>
                </CreditFieldContainer>

                <div className="flex items-center gap-[1rem] pr-[2rem]">
                  {credit.isActive ? (
                    <p className="font-semibold text-xxSmallSize w-[50px]">
                      Active
                    </p>
                  ) : (
                    <p className="text-colorCopyLight text-xxSmallSize w-[50px]">
                      Inactive
                    </p>
                  )}
                  <Icon
                    icon="trash"
                    color={theme.colorPrimary}
                    onClick={() => handleDeleteCompanyCredit(credit)}
                  />
                  <Icon
                    icon="edit"
                    color={theme.colorPrimary}
                    width="20px"
                    height="20px"
                    onClick={() => handleEditIconClick(credit)}
                  />
                </div>
              </SelectableContainer>
            ))}

            {showCompanyCreditDetails && creditForm && (
              <CreditDetails
                companyId={props.companyId}
                credit={creditForm}
                creditType={CreditType.Company}
                onClickHide={() => setShowCompanyCreditDetails(false)}
                refreshCredits={handleGetCompanyCredits}
                isAdmin={props.isAdmin}
              />
            )}
          </CompanyCreditContainer>
        </LibraryInnerContainer>
        <BottomBar show={state.items && state.items.length ? true : false}>
          <p className={`bar-text`}>{`${state.items.length} File${
            state.items.length > 1 ? "s" : ""
          } Selected`}</p>
          <ToggleSwitchField
            id="allActive"
            className="mx-[1.5rem]"
            name="allActive"
            disabled={state.status === PENDING}
            checked={allActive}
            label={allActive ? "Active" : "Inactive"}
            small
            onClick={() => handleToggleActiveSelected(!allActive, state.items)}
          />
          <Button
            className="button-transparent flex-col w-[175px] max-w-[175px]"
            onClick={() =>
              state.status === PENDING ? {} : handleDeleteSelected()
            }
            icon="trash"
            iconSize="20px"
            iconColor={theme.colorPrimary}
          >
            Delete
          </Button>
          <Button
            className="button-transparent flex-col w-[175px] max-w-[175px]"
            onClick={() =>
              state.status === PENDING ? {} : setShowTagModal(true)
            }
            icon="tag"
            iconSize="20px"
            iconColor={theme.colorPrimary}
          >
            Tag
          </Button>
          <Button
            className="button-transparent flex-col w-[175px] max-w-[175px]"
            onClick={() => loadItems([])}
            icon="deselect"
            iconSize="20px"
            iconColor={theme.colorPrimary}
          >
            Deselect All
          </Button>
        </BottomBar>
      </LibraryContainer>
    </FormikProvider>
  );
};

export default CompanyCreditList;

interface CompanyCreditListProps {
  //   creditType: CreditType;
  companyId: number;
  isAdmin: boolean;
  refreshCredits: boolean;
}

export enum CSVStatus {
  Idle,
  Processing,
  Complete,
}
